import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Spinner, Badge, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useProductApi, { Product } from '../../../managers/ProductManager';
import { TEMPLATE_GROUPS, TemplateGroup } from '../../../managers/TemplateGroupManager';

interface GroupWithProduct {
  group: TemplateGroup;
  firstProduct: Product;
  productCount: number;
}

const TemplateGroupList: React.FC = () => {
  const navigate = useNavigate();
  const { getTemplateProducts } = useProductApi();
  const [groupsWithProducts, setGroupsWithProducts] = useState<GroupWithProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplateGroups = async () => {
      try {
        // Fetch all templates
        const templates = await getTemplateProducts();

        // Group templates by their first tag (template group)
        const groupMap = new Map<string, Product[]>();

        templates.forEach((template) => {
          const groupId = template.tags?.[0];
          if (groupId) {
            if (!groupMap.has(groupId)) {
              groupMap.set(groupId, []);
            }
            groupMap.get(groupId)!.push(template);
          }
        });

        // Create GroupWithProduct objects for groups that have templates
        const groupsData: GroupWithProduct[] = TEMPLATE_GROUPS
          .filter(group => groupMap.has(group.id))
          .map(group => ({
            group,
            firstProduct: groupMap.get(group.id)![0],
            productCount: groupMap.get(group.id)!.length
          }))
          .sort((a, b) => a.group.priority - b.group.priority);

        setGroupsWithProducts(groupsData);
      } catch (err) {
        console.error('Error fetching template groups', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplateGroups();
  }, []);

  const handleGroupClick = (groupId: string) => {
    navigate(`/templates/${groupId}`);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner size="sm" animation="border" />
      </div>
    );
  }

  if (groupsWithProducts.length === 0) {
    return null; // Don't show section if no template groups
  }

  return (
    <Container>
      <h2 className="text-center mb-3">Customize a Template</h2>
      <p className="text-center text-muted mb-4">
        Choose a category below and customize a design to make it yours
      </p>

      <Row>
        {groupsWithProducts.map(({ group, firstProduct, productCount }) => {
          const GroupIcon = group.icon;

          return (
            <Col key={group.id} xs={6} sm={6} md={4} lg={3}>
              <Card
                className="mb-4 h-100 border-0 ws-img-clickable"
                onClick={() => handleGroupClick(group.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="position-relative">
                  <Card.Img
                    variant="top"
                    src={firstProduct.previewImageUrls?.[0]}
                    alt={group.name}
                    className="img-fluid"
                  />
                  <Badge
                    bg="primary"
                    className="position-absolute top-0 end-0 m-2"
                    style={{ fontSize: '0.85rem', fontWeight: 'bold' }}
                  >
                    {productCount} {productCount === 1 ? 'Template' : 'Templates'}
                  </Badge>

                  {/* Group Name Overlay */}
                  <div className="position-absolute bottom-0 start-50 translate-middle-x w-100" style={{ zIndex: 1 }}>
                    <div
                      className="d-flex flex-column align-items-center text-center bg-light text-dark px-2 py-2 shadow-sm"
                      style={{ fontSize: '1rem', opacity: 0.95 }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <GroupIcon size={20} />
                        <span className="fw-bold">
                          {group.name}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </Container>
  );
};

export default TemplateGroupList;
